import type {
  CopilotReviewerRow,
  PendingReviewRequest,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {announce} from '@github-ui/aria-live'
import {Button} from '@primer/react'
import {Tooltip} from '@primer/react/next'
import {useRef} from 'react'
import {clsx} from 'clsx'
import styles from '../Reviewers.module.css'
import {CopilotReviewEffortMenu} from '../CopilotReviewEffortMenu'
import {DeferredStatusIcon, ReviewStatusDetails} from '../ReviewStatusIcons'
import {ReviewerIdentity, reviewerDisplayName} from '../ReviewerIdentity'
import {deferredTooltip} from '../review-state-helpers'
import {useStagedCopilotReviewEffort} from '../use-staged-copilot-review-effort'
import {useModifyReviewers} from '../../../../mutations/use-modify-reviewers'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'

export function DeferredCopilotReviewRow({
  request,
  row,
  onMutationStart,
  onMutationError,
  onMutationSuccess,
  onCloseWithoutTarget,
}: {
  request: PendingReviewRequest
  row: CopilotReviewerRow
  onMutationStart: () => void
  onMutationError: (origin: Element | null) => void
  onMutationSuccess: () => void
  onCloseWithoutTarget?: () => void
}) {
  const mutationOriginRef = useRef<Element | null>(null)
  const {mutate: modifyReviewers, isPending} = useModifyReviewers({
    onError: () => onMutationError(mutationOriginRef.current),
    onSuccess: onMutationSuccess,
  })
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()
  const {shownEffort, explicitEffort, stageEffort} = useStagedCopilotReviewEffort(row, row.requestable)
  const reviewer = request.reviewer
  if (!reviewer) return null

  const name = reviewerDisplayName(reviewer)
  const statusLabel = deferredTooltip(request.deferredType, name)
  const showEffortMenu = row.requestable && row.reviewEffortOptions.length > 0
  const requestNow = () => {
    if (isPending) return
    mutationOriginRef.current = document.activeElement
    onMutationStart()
    announce(`Requesting review from ${name}`)
    sendPullRequestAnalyticsEvent('overview.request_copilot_review', 'OVERVIEW_REVIEWER', {
      review_effort: explicitEffort ?? 'default',
    })
    modifyReviewers({
      addReviewers: [
        {
          reviewerId: Number(reviewer.id),
          isTeam: false,
          ...(explicitEffort ? {reviewEffortLevel: explicitEffort} : {}),
        },
      ],
      removeReviewers: [],
    })
  }

  return (
    <li className={clsx(styles.reviewerRow, showEffortMenu && styles.withEffortMenu)}>
      <ReviewStatusDetails label={statusLabel}>
        {showEffortMenu ? (
          <span className={styles.reviewerIdentityGroup} data-copilot-deferred-review-effort-picker>
            <ReviewerIdentity reviewer={reviewer} />
            <input
              type="hidden"
              name="review_effort_level"
              value={explicitEffort ?? ''}
              data-testid="copilot-deferred-review-effort"
            />
            <span className={styles.effortMenu}>
              <CopilotReviewEffortMenu
                row={row}
                selectedEffort={shownEffort}
                disabled={isPending}
                onCloseWithoutTarget={onCloseWithoutTarget}
                onSelectEffort={effort => {
                  sendPullRequestAnalyticsEvent('overview.copilot_review_effort_select', 'OVERVIEW_REVIEWER', {
                    review_effort: effort,
                  })
                  stageEffort(effort)
                }}
              />
            </span>
          </span>
        ) : (
          <ReviewerIdentity reviewer={reviewer} />
        )}
      </ReviewStatusDetails>
      <span className={styles.indicators}>
        {row.requestable ? (
          <Tooltip text={`Request a review from ${name} now`} type="label">
            <Button
              variant="link"
              className={styles.requestButton}
              inactive={isPending || undefined}
              aria-disabled={isPending || undefined}
              onClick={requestNow}
            >
              Request
            </Button>
          </Tooltip>
        ) : null}
        <DeferredStatusIcon label={statusLabel} />
      </span>
    </li>
  )
}
